/**
 * 内部审计门户 - 主应用
 * 重构版：以审计专栏为主界面，支持用户管理
 * 支持 ?standalone=complaint 独立投诉举报表单、?standalone=complaint-status 投诉人查询（均无需登录）
 * 支持 ?demo=1 访客演示（需服务端 ALLOW_PUBLIC_DEMO=true 与演示账号，见 env.example）
 */
import React, { Suspense, lazy, useState, useEffect } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import { AppDataProvider } from './contexts/AppDataContext';
import { AIProvider } from './contexts/AIContext';
import { UserProvider, useUser, LoginPage } from './components/UserManagement';
import { ToastProvider } from './components/Toast';
import ComplaintFormStandalone from './components/SupervisionAudit/ComplaintFormStandalone';
import ComplaintStatusStandalone from './components/SupervisionAudit/ComplaintStatusStandalone';
import RectificationReplyStandalone from './components/SupervisionAudit/RectificationReplyStandalone';
import AssessmentFillStandalone from './components/RiskMatrixView/AssessmentFillStandalone';
import BrainstormStandalone from './components/Brainstorm/BrainstormStandalone';
import { logError } from './services/auditLogService';
import { startUnifiedModuleDataSync } from './services/unifiedModuleDataSync';
import DemoModeBanner from './components/DemoModeBanner';

const CherryStudio = lazy(() => import('./components/CherryStudio'));
const RiskDetailStandalone = lazy(() => import('./components/RiskMatrixView/RiskDetailStandalone'));
const ITAuditTicketSystemStandalone = lazy(() => import('./components/ITAuditModule/ITAuditTicketSystemStandalone'));

// 懒加载主界面
const InternalAuditPortal = lazy(() => import('./components/InternalAuditPortal'));
const UIShowcase = lazy(() => import('./components/UIShowcase'));

const ICP_RECORD = '闽ICP备2026002379号-1';

function SiteIcpFooter() {
  return (
    <a
      className="site-icp-footer"
      href="https://beian.miit.gov.cn/"
      target="_blank"
      rel="noopener noreferrer"
    >
      {ICP_RECORD}
    </a>
  );
}

// 加载占位符
const LoadingScreen = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
    <div className="text-center">
      <div className="relative w-20 h-20 mx-auto mb-6">
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-400 to-purple-400 blur-xl opacity-50 animate-pulse" />
        <div className="absolute inset-0">
          <svg className="w-full h-full animate-spin" style={{ animationDuration: '2s' }} viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" stroke="url(#grad)" strokeWidth="4" strokeLinecap="round" strokeDasharray="180 100" />
            <defs>
              <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6366f1" />
                <stop offset="100%" stopColor="#a855f7" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>
      <p className="text-gray-500 text-lg">加载中...</p>
    </div>
  </div>
);

// 全局报错写入审计日志
function AuditLogErrorHandler() {
  useEffect(() => {
    const onError = (message, source, lineno, colno, error) => {
      logError(message, source, lineno, colno, error);
    };
    const onUnhandledRejection = (e) => {
      const msg = e.reason?.message || e.reason?.toString?.() || String(e.reason);
      const stack = e.reason?.stack;
      logError(msg, undefined, undefined, undefined, stack ? { stack } : undefined);
    };
    window.onerror = onError;
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.onerror = null;
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);
  return null;
}

// 解析 URL 参数（仅初始化时读取一次）
function parseUrlParams() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const pathname = (window.location.pathname || '/').replace(/\/+$/, '') || '/';
  let standalone = params.get('standalone');
  if (!standalone) {
    if (pathname === '/complaint') standalone = 'complaint';
    else if (pathname === '/complaint-status') standalone = 'complaint-status';
  }
  return {
    standalone,
    mode: params.get('mode'),
    token: params.get('token'),
    module: params.get('module'),
    cherryLayout: params.get('cherryLayout'),
  };
}

function ForcePasswordChangeModal() {
  const { user, changePassword, logout } = useUser();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const validate = () => {
    if (!currentPassword || !newPassword || !confirmPassword) return '请完整填写密码信息';
    if (newPassword !== confirmPassword) return '两次输入的新密码不一致';
    if (newPassword.length < 12) return '新密码长度至少 12 位';
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword) || !/[!@#$%^&*()_\-+=\[\]{};:'",.<>/?\\|`~]/.test(newPassword)) {
      return '新密码需包含大小写字母、数字和特殊字符';
    }
    if (user?.username && newPassword.toLowerCase().includes(user.username.toLowerCase())) return '新密码不能包含用户名';
    return '';
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const invalid = validate();
    if (invalid) {
      setError(invalid);
      return;
    }
    setError('');
    setSubmitting(true);
    const result = await changePassword(currentPassword, newPassword);
    if (!result.success) {
      setError(result.error || '修改密码失败');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
          <h2 className="text-lg font-semibold">首次登录请修改密码</h2>
          <p className="text-sm text-white/90 mt-1">为保障数据安全，修改完成前无法进入核心页面</p>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">当前密码</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">新密码</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              autoComplete="new-password"
            />
            <p className="text-xs text-gray-500 mt-1">至少12位，需含大小写字母、数字和特殊字符</p>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">确认新密码</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              autoComplete="new-password"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={logout}
              className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              退出登录
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60"
            >
              {submitting ? '提交中...' : '确认修改'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** 演示模式顶栏占位：fixed 条不占据文档流时，为下方内容留出顶部间距 */
function DemoLayoutShell({ children }) {
  const { user, loading } = useUser();
  const showDemoBar = !loading && Boolean(user?.isDemoGuest);
  return (
    <div className={showDemoBar ? 'pt-11 min-h-screen' : 'min-h-screen'}>
      {showDemoBar ? <DemoModeBanner /> : null}
      {children}
    </div>
  );
}

// 主应用内容（需要用户登录；独立投诉页无需登录）
function AppContent() {
  const [urlParams] = useState(parseUrlParams);
  const { isLoggedIn, loading, user } = useUser();

  // WebView 模式：移动端通过 URL 传入 token 和目标模块
  const isWebviewMode = urlParams.mode === 'webview';

  useEffect(() => {
    // standalone 页面不走 portal-scale-host（主门户内部滚动容器），需要恢复页面级滚动
    const standalone = String(urlParams.standalone || '').trim();
    if (!standalone) return undefined;

    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflowY = html.style.overflowY;
    const prevBodyOverflowY = body.style.overflowY;

    html.style.overflow = 'auto';
    body.style.overflow = 'auto';
    html.style.overflowY = 'auto';
    body.style.overflowY = 'auto';

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.style.overflowY = prevHtmlOverflowY;
      body.style.overflowY = prevBodyOverflowY;
    };
  }, [urlParams.standalone]);

  useEffect(() => {
    // 统一模块数据持久化：将关键 localStorage 数据自动同步到服务器。
    startUnifiedModuleDataSync();
  }, []);

  useEffect(() => {
    if (isWebviewMode && urlParams.token) {
      localStorage.setItem('token', urlParams.token);
      document.documentElement.setAttribute('data-webview', 'true');
    }
    return () => {
      document.documentElement.removeAttribute('data-webview');
    };
  }, [isWebviewMode, urlParams.token]);

  if (urlParams.standalone === 'complaint') {
    return <ComplaintFormStandalone />;
  }
  if (urlParams.standalone === 'complaint-status') {
    return <ComplaintStatusStandalone />;
  }
  if (urlParams.standalone === 'rectification-reply') {
    return <RectificationReplyStandalone />;
  }
  if (urlParams.standalone === 'assessment-fill') {
    return <AssessmentFillStandalone />;
  }
  if (urlParams.standalone === 'brainstorm') {
    return <BrainstormStandalone />;
  }
  if (urlParams.standalone === 'chat') {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <CherryStudio onClose={() => window.history.back()} embedded={false} />
      </Suspense>
    );
  }
  if (urlParams.standalone === 'ui-showcase') {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <UIShowcase />
      </Suspense>
    );
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isLoggedIn) {
    return <LoginPage />;
  }
  if (urlParams.standalone === 'risk-detail') {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <RiskDetailStandalone />
      </Suspense>
    );
  }

  if (urlParams.standalone === 'it-ticket-system') {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <ITAuditTicketSystemStandalone />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <div className="portal-scale-host">
        <div className="portal-scale-root">
          <InternalAuditPortal
            isOpen={true}
            embedded={false}
            isMainApp={true}
            initialModule={urlParams.module || null}
            initialCherryLayout={urlParams.cherryLayout || null}
          />
        </div>
      </div>
    </Suspense>
  );
}


// 根组件
export default function App() {
  return (
    <ThemeProvider>
      <AppDataProvider>
        <AIProvider>
          <UserProvider>
            <ToastProvider>
              <AuditLogErrorHandler />
              <DemoLayoutShell>
                <AppContent />
                <SiteIcpFooter />
              </DemoLayoutShell>
            </ToastProvider>
          </UserProvider>
        </AIProvider>
      </AppDataProvider>
    </ThemeProvider>
  );
}
